import styled from 'styled-components';
import { ChangeEvent, Dispatch, SetStateAction, useRef, useState, useEffect } from 'react';
import { BtnGray, BtnRed, BtnBlue } from './Button';
import FileUploadButton from './button/FileUploadButton';
import { useTranslation } from 'react-i18next';
import { useSetAuth } from '../utils/useSetAuth';
import { apiDelete } from '../services/_common';
import Portal from './Portal';
import DeleteModal from './Modal/DeleteModal2';
import { userState } from '../atoms';
import { useRecoilValue } from 'recoil';
import { toast } from 'react-toastify';
import { logPost } from '../services/log';
import ReactSignatureCanvas from 'react-signature-canvas';

const Root = styled.div`
  div[data-disabled='true'] {
    pointer-events: none;
    cursor: not-allowed;
  }

  .canvas_wrap {
    position: relative;
    display: flex;
    width: 100%;
    aspect-ratio: 600 / 384; // PC와 동일한 비율 유지 (600:384)
    max-width: 600px; // PC에서 최대 크기 제한
    max-height: 384px; // PC에서 최대 높이 제한
    margin-bottom: 1.2rem;

    .signature-canvas {
      border: 2px dashed #e1e5ea;
      text-align: left;
      padding: 4px;
      width: 100%;
      height: 100%;
    }

    small {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 0.875rem;
    }
  }

  .btn_wrap {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0 0 1rem;

    .btn_left,
    .btn_right {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      button {
        height: 2.5rem;
      }
    }
  }
`;
// export const emptyImg = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAF0lEQVR4nGP8//8/AzGAiShVowqpphAA1RIDEZR7aoQAAAAASUVORK5CYII=';

interface Props {
  isSigned: boolean;
  setIsSigned: Dispatch<SetStateAction<boolean>>;
  dataURL: string;
  setDataURL: Dispatch<SetStateAction<string>>;
  onClickSave: () => void;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

const SignaturePad = ({ isSigned, setIsSigned, dataURL, setDataURL, onClickSave, isLoading, setIsLoading }: Props) => {
  const { auth } = useSetAuth();
  const userInfo = useRecoilValue(userState);
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState<any>({ type: '', status: false });
  const canvasRef = useRef<ReactSignatureCanvas>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // dataURL이 변경될 때만 캔버스에 로드 (중복 로드 방지)
  // 이미지를 직접 그린 경우나 서명 중에는 실행하지 않음
  const prevDataURLRef = useRef<string>('');
  useEffect(() => {
    // dataURL이 실제로 변경되었고, 이미지를 직접 그린 경우가 아니며, 빈 문자열이 아닐 때만 로드
    if (canvasRef.current && dataURL && dataURL !== prevDataURLRef.current && !isImageLoaded && dataURL.trim() !== '') {
      // 외부에서 dataURL이 변경된 경우에만 로드 (예: 초기 로드, 저장된 이미지 불러오기)
      canvasRef.current.fromDataURL(dataURL);
      prevDataURLRef.current = dataURL;
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [dataURL, isImageLoaded]);

  // 초기 로드 시 캔버스 크기를 표시 크기에 맞게 설정
  // react-signature-canvas가 자동으로 크기를 관리하므로 빈 캔버스일 때만 설정
  useEffect(() => {
    const initCanvasSize = () => {
      if (canvasRef.current) {
        const canvas = canvasRef.current.getCanvas();
        const rect = canvas.getBoundingClientRect();
        const displayWidth = Math.round(rect.width) || canvas.offsetWidth || 600;
        const displayHeight = Math.round(rect.height) || canvas.offsetHeight || 384;

        // 빈 캔버스일 때만 초기 크기 설정
        if (canvas.width === 0 || canvas.height === 0) {
          canvas.width = displayWidth;
          canvas.height = displayHeight;
        }
      }
    };

    // 초기 크기 설정을 위한 타이머 (DOM이 완전히 렌더링된 후)
    const timer = setTimeout(() => {
      initCanvasSize();
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // 서명 파일 불러오기
  const onChangeImg = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const file = e.target.files[0];
      const reader = new FileReader();

      if (canvasRef.current) {
        reader.onloadend = () => {
          const img = new Image();
          img.src = String(reader.result);

          img.onload = () => {
            // 캔버스 초기화
            canvasRef.current?.clear();
            setIsImageLoaded(true);

            // 캔버스 가져오기
            const canvas = canvasRef.current?.getCanvas();
            if (!canvas) return;

            // 캔버스의 실제 표시 크기 가져오기 (모바일 대응)
            const rect = canvas.getBoundingClientRect();
            const displayWidth = Math.round(rect.width) || canvas.offsetWidth || canvas.clientWidth || 600;
            const displayHeight = Math.round(rect.height) || canvas.offsetHeight || canvas.clientHeight || 384;

            // 임시 캔버스를 생성하여 이미지를 올바른 크기로 리사이즈
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = displayWidth;
            tempCanvas.height = displayHeight;
            const tempCtx = tempCanvas.getContext('2d');
            if (!tempCtx) return;

            // 표시 크기를 기준으로 이미지 비율 계산 (object-fit: contain)
            const imageWidth = img.width;
            const imageHeight = img.height;
            const ratio = Math.min(displayWidth / imageWidth, displayHeight / imageHeight);
            const scaledWidth = imageWidth * ratio;
            const scaledHeight = imageHeight * ratio;
            const x = (displayWidth - scaledWidth) / 2;
            const y = (displayHeight - scaledHeight) / 2;

            // 임시 캔버스에 이미지 그리기 (비율 유지 및 중앙 정렬)
            tempCtx.drawImage(img, x, y, scaledWidth, scaledHeight);

            // 리사이즈된 이미지를 dataURL로 변환하여 react-signature-canvas에 로드
            const resizedDataURL = tempCanvas.toDataURL('image/png');
            canvasRef.current?.fromDataURL(resizedDataURL);

            // 업로드한 이미지를 바로 저장할 수 있도록 dataURL 상태에 반영
            setDataURL(resizedDataURL);

            setIsSigned(true);
            setIsImageLoaded(true); // 이미지 로드 완료 플래그 설정
          };
        };
        reader.readAsDataURL(file);
      }
    }
  };

  // 개인서명 삭제
  const onClickDelete = async () => {
    setOpenModal({ type: 'delete', status: true, api: deleteAPI });
  };

  const deleteAPI = async () => {
    const req = { cCd: userInfo.hCd };
    const res = await apiDelete({ path: '/company/sign', req });

    if (res.status === 200) {
      canvasRef.current?.clear();
      setIsSigned(false);
      setDataURL('');
      prevDataURLRef.current = '';
      setIsImageLoaded(false);
      toast.success('삭제하였습니다.');
      await logPost({
        hCd: userInfo.hCd,
        sCd: userInfo.sCd,
        userId: userInfo.userId,
        menu: '기타 관리 > 개인 서명 관리',
        action: `개인 서명 삭제`,
        etc: `개인 서명 삭제`,
      });
    }
  };

  return (
    <Root>
      <div className='canvas_wrap'>
        {!isSigned && (
          <small className='text-5xl text-slate-200'>{t(isLoading && dataURL?.trim() !== '' ? '서명 이미지를 불러오는 중입니다' : !auth.createAuth ? '등록 권한이 없습니다' : '개인 서명란')}</small>
        )}
        <div data-disabled={!auth.createAuth || isLoading}>
          <ReactSignatureCanvas
            penColor='black'
            velocityFilterWeight={1}
            minWidth={2}
            maxWidth={4}
            canvasProps={{
              style: {
                width: '100%',
                height: '100%',
                pointerEvents: isLoading ? 'none' : 'auto',
              },
              className: 'signature-canvas w-full h-full',
            }}
            ref={canvasRef}
            backgroundColor='transparent'
            clearOnResize={false}
            onBegin={() => {
              setIsSigned(true);
            }}
            onEnd={() => {
              // 서명이 끝날 때 현재 캔버스 상태를 dataURL로 저장
              // 이미지가 로드된 상태에서 서명을 추가했으므로 플래그는 유지
              const img = canvasRef.current?.toDataURL('image/jpg') || '';
              // useEffect의 fromDataURL 재적용 루프 방지
              prevDataURLRef.current = img;
              setDataURL(img);
            }}
          />
        </div>
      </div>

      <div className='btn_wrap'>
        <div className='btn_left'>
          <BtnGray
            onClick={() => {
              canvasRef.current?.clear(); // 리셋
              setIsSigned(false);
              setDataURL('');
              prevDataURLRef.current = '';
              setIsImageLoaded(false); // 이미지 로드 플래그 리셋
            }}
          >
            {t('지우기')}
          </BtnGray>
          <FileUploadButton
            id='sign'
            name='sign'
            label='서명 파일 불러오기'
            ico='upload'
            accept='image/*'
            multiple='N'
            handleChange={(e: ChangeEvent<HTMLInputElement>) => onChangeImg(e)}
            disabled={!auth.createAuth}
          />
        </div>

        <div className='btn_right'>
          <BtnRed onClick={onClickDelete} disabled={!auth.deleteAuth}>
            {t('삭제')}
          </BtnRed>
          <BtnBlue onClick={onClickSave} disabled={!auth.createAuth}>
            {t('저장')}
          </BtnBlue>
        </div>
      </div>

      <Portal openModal={openModal?.status}>{openModal && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}</Portal>
    </Root>
  );
};

export default SignaturePad;
